import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarDaysIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FlagIcon,
} from '@heroicons/react/24/outline';
import {
  PortalPlanningData,
  PortalPlanningPhase,
  PortalPlanningTaskStatus,
} from './portalPlanningMock';
import {
  PlanningCalendar,
  PlanningMilestonesList,
  PlanningScheduleList,
} from './PortalProjectPlanningViews';

const WEEK_WIDTH = 44;
const LABEL_COLUMNS_WIDTH = 520;

type PlanningView = 'gantt' | 'schedule' | 'calendar' | 'milestones';

const viewOptions: { id: PlanningView; label: string }[] = [
  { id: 'gantt', label: 'Gráfico de Gantt' },
  { id: 'schedule', label: 'Cronograma' },
  { id: 'calendar', label: 'Calendário' },
  { id: 'milestones', label: 'Marcos' },
];

const barClass: Record<PortalPlanningTaskStatus, string> = {
  done: 'bg-emerald-500',
  inProgress: 'bg-primary-500',
  planned: 'bg-primary-200',
  notStarted: 'bg-secondary-300',
};

function parseDate(iso: string): Date {
  const [year, month, day] = iso.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
}

function daysBetween(start: Date, end: Date): number {
  const ms = end.getTime() - start.getTime();
  return Math.max(0, Math.round(ms / (24 * 60 * 60 * 1000)));
}

function formatShortDate(iso: string): string {
  const date = parseDate(iso);
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function monthLabel(date: Date): string {
  const label = date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function buildWeekColumns(timelineStart: string, timelineEnd: string): {
  weeks: { key: string; start: Date; label: string; monthKey: string }[];
  months: { key: string; label: string; weekCount: number }[];
} {
  const start = parseDate(timelineStart);
  const end = parseDate(timelineEnd);
  const weeks: { key: string; start: Date; label: string; monthKey: string }[] = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);

  while (cursor <= end) {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
    for (let weekIndex = 0; weekIndex < 4; weekIndex += 1) {
      const weekStart = new Date(year, month, 1 + weekIndex * 7);
      if (weekStart > end) break;
      if (weekStart < start && weekIndex < 3) {
        const nextWeek = new Date(year, month, 1 + (weekIndex + 1) * 7);
        if (nextWeek <= start) continue;
      }
      weeks.push({
        key: `${monthKey}-w${weekIndex + 1}`,
        start: weekStart,
        label: `S${weekIndex + 1}`,
        monthKey,
      });
    }
    cursor.setMonth(cursor.getMonth() + 1);
  }

  const months: { key: string; label: string; weekCount: number }[] = [];
  for (const week of weeks) {
    const existing = months.find((month) => month.key === week.monthKey);
    if (existing) {
      existing.weekCount += 1;
    } else {
      months.push({
        key: week.monthKey,
        label: monthLabel(week.start),
        weekCount: 1,
      });
    }
  }

  return { weeks, months };
}

function barStyle(
  itemStart: string,
  itemEnd: string,
  timelineStart: string,
  timelineEnd: string,
  totalWidth: number,
): { left: number; width: number } {
  const start = parseDate(timelineStart);
  const end = parseDate(timelineEnd);
  const totalDays = Math.max(daysBetween(start, end), 1);
  const itemStartDate = parseDate(itemStart);
  const itemEndDate = parseDate(itemEnd);
  const offsetDays = Math.max(0, daysBetween(start, itemStartDate));
  const durationDays = Math.max(daysBetween(itemStartDate, itemEndDate), 1);
  const left = (offsetDays / totalDays) * totalWidth;
  const width = Math.max((durationDays / totalDays) * totalWidth, 8);
  return { left, width };
}

function todayOffset(
  todayDate: string,
  timelineStart: string,
  timelineEnd: string,
  totalWidth: number,
): number | null {
  const today = parseDate(todayDate);
  const start = parseDate(timelineStart);
  const end = parseDate(timelineEnd);
  if (today < start || today > end) return null;
  const totalDays = Math.max(daysBetween(start, end), 1);
  return (daysBetween(start, today) / totalDays) * totalWidth;
}

type PortalProjectPlanningProps = {
  planning: PortalPlanningData;
  projectPath: string;
};

export function PortalProjectPlanning({ planning, projectPath }: PortalProjectPlanningProps) {
  const [view, setView] = useState<PlanningView>('gantt');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const initialMonth = parseDate(planning.todayDate);
  const [visibleMonth, setVisibleMonth] = useState(
    () => new Date(initialMonth.getFullYear(), initialMonth.getMonth(), 1),
  );
  const { weeks, months } = useMemo(
    () => buildWeekColumns(planning.timelineStart, planning.timelineEnd),
    [planning.timelineEnd, planning.timelineStart],
  );
  const timelineWidth = weeks.length * WEEK_WIDTH;
  const todayLeft = todayOffset(
    planning.todayDate,
    planning.timelineStart,
    planning.timelineEnd,
    timelineWidth,
  );

  function togglePhase(phaseId: string) {
    setCollapsed((current) => ({ ...current, [phaseId]: !current[phaseId] }));
  }

  function goToToday() {
    const today = parseDate(planning.todayDate);
    setVisibleMonth(new Date(today.getFullYear(), today.getMonth(), 1));
  }

  function shiftMonth(delta: number) {
    setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() + delta, 1));
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-secondary-900">Planejamento e Cronograma</h2>
          <p className="mt-1 max-w-2xl text-sm text-secondary-600">
            Acompanhe as etapas, prazos e marcos do projeto. O cronograma é atualizado em tempo real.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="rounded-xl border border-secondary-200 bg-white px-4 py-3">
            <p className="text-xs font-medium text-secondary-500">Período do projeto</p>
            <p className="mt-1 flex items-center gap-2 text-sm font-semibold text-secondary-900">
              <CalendarDaysIcon className="h-4 w-4 text-secondary-500" aria-hidden />
              {planning.periodStartLabel}
              <span className="text-secondary-400">→</span>
              {planning.periodEndLabel}
            </p>
          </div>
          <div className="flex items-center gap-3 rounded-xl border border-secondary-200 bg-white px-4 py-3">
            <ProgressRing percent={planning.progressPercent} />
            <div>
              <p className="text-xs font-medium text-secondary-500">Progresso geral</p>
              <p className="text-sm font-semibold text-emerald-600">{planning.progressDeltaLabel}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {viewOptions.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => setView(option.id)}
              className={`rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                view === option.id
                  ? 'bg-primary-500 text-white'
                  : 'border border-secondary-200 bg-white text-secondary-700 hover:bg-secondary-50'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-700"
          >
            Visão: Detalhada
            <ChevronDownIcon className="h-4 w-4 text-secondary-400" aria-hidden />
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-700"
          >
            Período: Mensal
            <ChevronDownIcon className="h-4 w-4 text-secondary-400" aria-hidden />
          </button>
          <button
            type="button"
            onClick={goToToday}
            className="rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm font-semibold text-secondary-700"
          >
            Hoje
          </button>
          <button
            type="button"
            onClick={() => shiftMonth(-1)}
            className="rounded-lg border border-secondary-200 bg-white p-2 text-secondary-600"
            aria-label="Período anterior"
          >
            <ChevronLeftIcon className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => shiftMonth(1)}
            className="rounded-lg border border-secondary-200 bg-white p-2 text-secondary-600"
            aria-label="Próximo período"
          >
            <ChevronRightIcon className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>

      {view === 'gantt' ? (
        <GanttBoard
          phases={planning.phases}
          collapsed={collapsed}
          onTogglePhase={togglePhase}
          weeks={weeks}
          months={months}
          timelineWidth={timelineWidth}
          timelineStart={planning.timelineStart}
          timelineEnd={planning.timelineEnd}
          todayLeft={todayLeft}
        />
      ) : null}
      {view === 'schedule' ? <PlanningScheduleList phases={planning.phases} /> : null}
      {view === 'calendar' ? <PlanningCalendar planning={planning} visibleMonth={visibleMonth} /> : null}
      {view === 'milestones' ? (
        <PlanningMilestonesList milestones={planning.milestones} todayDate={planning.todayDate} />
      ) : null}

      {view !== 'milestones' ? (
        <section className="rounded-2xl border border-secondary-200 bg-white p-5">
          <h3 className="text-base font-semibold text-secondary-900">Marcos do projeto</h3>
          <ol className="mt-5 flex gap-0 overflow-x-auto pb-1">
            {planning.milestones.map((milestone, index) => {
              const isLast = index === planning.milestones.length - 1;
              return (
                <li key={milestone.id} className="relative flex min-w-[8rem] flex-1 flex-col items-center px-2">
                  {!isLast ? (
                    <span
                      className={`absolute left-[calc(50%+0.85rem)] right-[-0.35rem] top-3 h-0.5 ${
                        milestone.status === 'done' ? 'bg-emerald-400' : 'bg-secondary-200'
                      }`}
                      aria-hidden
                    />
                  ) : null}
                  <span
                    className={`relative z-10 flex h-6 w-6 items-center justify-center rounded-full border-2 ${
                      milestone.status === 'done'
                        ? 'border-emerald-500 bg-emerald-500 text-white'
                        : milestone.status === 'current'
                          ? 'border-primary-500 bg-white text-primary-500'
                          : 'border-secondary-300 bg-white'
                    }`}
                  >
                    {milestone.status === 'done' ? (
                      <CheckIcon className="h-3.5 w-3.5" aria-hidden />
                    ) : milestone.status === 'current' ? (
                      <span className="h-2 w-2 rounded-full bg-primary-500" />
                    ) : null}
                  </span>
                  <p className="mt-3 text-center text-sm font-semibold text-secondary-900">{milestone.title}</p>
                  <p className="mt-0.5 text-center text-xs text-secondary-500">{milestone.dateLabel}</p>
                </li>
              );
            })}
          </ol>
        </section>
      ) : null}

      <section className="rounded-2xl border border-secondary-200 bg-white px-5 py-4">
        <h3 className="text-sm font-semibold text-secondary-900">Legenda</h3>
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-secondary-600">
          <LegendDot className="bg-emerald-500" label="Concluído" />
          <LegendDot className="bg-primary-500" label="Em andamento" />
          <LegendDot className="bg-primary-200" label="Planejado" />
          <LegendDot className="bg-secondary-300" label="Não iniciado" />
          <span className="inline-flex items-center gap-1.5">
            <span className="text-secondary-400">→</span>
            Dependência
          </span>
          <span className="inline-flex items-center gap-1.5">
            <FlagIcon className="h-3.5 w-3.5 text-secondary-700" aria-hidden />
            Marco
          </span>
        </div>
        <p className="mt-3 text-xs text-secondary-400">
          <Link to={projectPath} className="font-medium text-primary-700 hover:underline">
            Ver visão geral
          </Link>{' '}
          para o resumo executivo do projeto.
        </p>
      </section>
    </div>
  );
}

function GanttBoard({
  phases,
  collapsed,
  onTogglePhase,
  weeks,
  months,
  timelineWidth,
  timelineStart,
  timelineEnd,
  todayLeft,
}: {
  phases: PortalPlanningPhase[];
  collapsed: Record<string, boolean>;
  onTogglePhase: (phaseId: string) => void;
  weeks: { key: string; label: string }[];
  months: { key: string; label: string; weekCount: number }[];
  timelineWidth: number;
  timelineStart: string;
  timelineEnd: string;
  todayLeft: number | null;
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-secondary-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <div style={{ minWidth: LABEL_COLUMNS_WIDTH + timelineWidth }}>
          <div className="flex border-b border-secondary-200 bg-secondary-50">
            <div
              className="grid shrink-0 grid-cols-[minmax(0,1.6fr)_7rem_4.5rem_4.5rem_4.5rem] border-r border-secondary-200 text-xs font-medium text-secondary-500"
              style={{ width: LABEL_COLUMNS_WIDTH }}
            >
              <div className="px-4 py-3">Atividade</div>
              <div className="px-2 py-3">Responsável</div>
              <div className="px-2 py-3">Início</div>
              <div className="px-2 py-3">Término</div>
              <div className="px-2 py-3">Progresso</div>
            </div>
            <div style={{ width: timelineWidth }}>
              <div className="flex border-b border-secondary-200">
                {months.map((month) => (
                  <div
                    key={month.key}
                    className="border-r border-secondary-200 px-2 py-2 text-center text-xs font-semibold text-secondary-700 last:border-r-0"
                    style={{ width: month.weekCount * WEEK_WIDTH }}
                  >
                    {month.label}
                  </div>
                ))}
              </div>
              <div className="flex">
                {weeks.map((week) => (
                  <div
                    key={week.key}
                    className="border-r border-secondary-100 py-1.5 text-center text-[10px] font-medium text-secondary-400 last:border-r-0"
                    style={{ width: WEEK_WIDTH }}
                  >
                    {week.label}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {phases.map((phase) => {
            const isCollapsed = Boolean(collapsed[phase.id]);
            return (
              <div key={phase.id}>
                <GanttRow
                  kind="phase"
                  title={`${phase.number}. ${phase.title}`}
                  ownerName=""
                  startDate={phase.startDate}
                  endDate={phase.endDate}
                  progressPercent={phase.progressPercent}
                  status={phase.status}
                  expandable
                  expanded={!isCollapsed}
                  onToggle={() => onTogglePhase(phase.id)}
                  timelineWidth={timelineWidth}
                  timelineStart={timelineStart}
                  timelineEnd={timelineEnd}
                  todayLeft={todayLeft}
                />
                {!isCollapsed
                  ? phase.tasks.map((task) => (
                      <GanttRow
                        key={task.id}
                        kind="task"
                        title={task.title}
                        ownerName={task.ownerName}
                        startDate={task.startDate}
                        endDate={task.endDate}
                        progressPercent={task.progressPercent}
                        status={task.status}
                        isMilestone={task.isMilestone}
                        timelineWidth={timelineWidth}
                        timelineStart={timelineStart}
                        timelineEnd={timelineEnd}
                        todayLeft={todayLeft}
                      />
                    ))
                  : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function GanttRow({
  kind,
  title,
  ownerName,
  startDate,
  endDate,
  progressPercent,
  status,
  expandable,
  expanded,
  onToggle,
  isMilestone,
  timelineWidth,
  timelineStart,
  timelineEnd,
  todayLeft,
}: {
  kind: 'phase' | 'task';
  title: string;
  ownerName: string;
  startDate: string;
  endDate: string;
  progressPercent: number;
  status: PortalPlanningTaskStatus;
  expandable?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  isMilestone?: boolean;
  timelineWidth: number;
  timelineStart: string;
  timelineEnd: string;
  todayLeft: number | null;
}) {
  const style = barStyle(startDate, endDate, timelineStart, timelineEnd, timelineWidth);
  const isPhase = kind === 'phase';

  return (
    <div className={`flex border-b border-secondary-100 ${isPhase ? 'bg-secondary-50/70' : 'bg-white'}`}>
      <div
        className="grid shrink-0 grid-cols-[minmax(0,1.6fr)_7rem_4.5rem_4.5rem_4.5rem] items-center border-r border-secondary-200 text-sm"
        style={{ width: LABEL_COLUMNS_WIDTH }}
      >
        <div className={`flex items-center gap-2 px-4 py-2.5 ${isPhase ? '' : 'pl-8'}`}>
          {expandable ? (
            <button
              type="button"
              onClick={onToggle}
              className="rounded p-0.5 text-secondary-500 hover:bg-secondary-100"
              aria-label={expanded ? 'Recolher fase' : 'Expandir fase'}
            >
              <ChevronDownIcon className={`h-4 w-4 transition-transform ${expanded ? '' : '-rotate-90'}`} aria-hidden />
            </button>
          ) : status === 'done' ? (
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white">
              <CheckIcon className="h-3 w-3" aria-hidden />
            </span>
          ) : (
            <span className="h-4 w-4 rounded-full border border-secondary-300" />
          )}
          <span className={`truncate ${isPhase ? 'font-semibold text-secondary-900' : 'text-secondary-800'}`}>
            {title}
          </span>
          {isMilestone ? <FlagIcon className="h-3.5 w-3.5 shrink-0 text-secondary-500" aria-hidden /> : null}
        </div>
        <div className="truncate px-2 py-2.5 text-xs text-secondary-600">{ownerName || '—'}</div>
        <div className="px-2 py-2.5 text-xs text-secondary-600">{formatShortDate(startDate)}</div>
        <div className="px-2 py-2.5 text-xs text-secondary-600">{formatShortDate(endDate)}</div>
        <div className="px-2 py-2.5 text-xs font-semibold text-secondary-700">{progressPercent}%</div>
      </div>
      <div className="relative" style={{ width: timelineWidth, minHeight: 44 }}>
        {todayLeft != null ? (
          <div
            className="pointer-events-none absolute inset-y-0 z-10 w-px border-l border-dashed border-primary-400"
            style={{ left: todayLeft }}
          >
            {isPhase ? (
              <span className="absolute -top-0 left-1 rounded bg-primary-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">
                Hoje
              </span>
            ) : null}
          </div>
        ) : null}
        <div
          className={`absolute top-1/2 h-3 -translate-y-1/2 rounded-full ${barClass[status]} ${
            isPhase ? 'h-3.5 opacity-90' : ''
          }`}
          style={{ left: style.left, width: style.width }}
          title={`${title} · ${progressPercent}%`}
        />
      </div>
    </div>
  );
}

function ProgressRing({ percent }: { percent: number }) {
  const radius = 22;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <div className="relative h-14 w-14 shrink-0">
      <svg className="h-14 w-14 -rotate-90" viewBox="0 0 56 56" aria-hidden>
        <circle cx="28" cy="28" r={radius} fill="none" stroke="#E5E7EB" strokeWidth="5" />
        <circle
          cx="28"
          cy="28"
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="text-primary-500"
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-secondary-900">
        {percent}%
      </span>
    </div>
  );
}

function LegendDot({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`h-2.5 w-2.5 rounded-full ${className}`} />
      {label}
    </span>
  );
}
